import { createAction } from 'redux-actions';

import { resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';

import { appendMode } from 'constants/general';
import fetch from 'features/common/fetch';
import { saveFile } from 'features/common/fileSave/actions';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import userContext from 'features/views/userContext';
import { generateCommonFetchActions, trimFormData } from 'utils';
import { getDate3MonthsAgoAsDateObj } from 'utils/apiDateTime';

import * as types from './actionTypes';
import { formDataFieldsMap, quickFilter, SERVICES } from './constants';
import { CONTACT_HISTORY_FORM_NAME, FIELDS } from './form';
import { getQuickFilterState, getSort } from './selectors';
import { queryParamsFactory } from './utils';

export const appendCommunicationGroups = createAction(types.APPEND_COMMUNICATION_GROUPS, (groups) => ({ groups }));
export const changeQuickFilter = createAction(types.CHANGE_QUICK_FILTER, (type) => ({ type }));
export const reset = createAction(types.RESET);
export const selectCommunicationGroup = createAction(types.SELECT_COMMUNICATION_GROUP, (groupId) => ({ groupId }));
export const setCommunicationGroups = createAction(types.SET_COMMUNICATION_GROUPS, (groups) => ({ groups }));
export const setSort = createAction(types.SET_SORT, (sort) => ({ sort }));

const url = fetch.constants.urls.communicationHistory;

const fetchData =
	({ queryParams, callGtmFilter, formData }, append) =>
	(dispatch, getState) => {
		const contextUser = userContext.selectors.getContextUser(getState());
		const quickFilterType = getQuickFilterState(getState());
		let finalFormData;
		dispatch(
			fetch.actions.get(url, {
				takeLatest: true,
				queryParams,
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				onSuccess: [
					(response) => () => {
						const data = response.body.data;
						if (!data || !Array.isArray(data)) return;

						// GTM called only on basic search/filter, not on sort/loadMore/loadPage etc.
						if (callGtmFilter) {
							if (quickFilterType) {
								finalFormData = {};
								switch (quickFilterType) {
									case quickFilter.CANCEL_REQUEST:
										finalFormData.quickFilterCancel = true;
										break;
									case quickFilter.SERVICES_SERVICES:
										finalFormData.quickFilterServices = true;
										break;
									case quickFilter.BUSINESS_SERVICES:
										finalFormData.quickFilterBusiness = true;
										break;
								}
							} else {
								const cleansedFormData = trimFormData(formData);
								finalFormData = { ...cleansedFormData };
							}

							pushToFilterDataLayer({
								filterSection: 'Kontaktní historie',
								formData: finalFormData,
								formDataFieldsMap,
							});
						}

						dispatch(
							append === appendMode.APPEND
								? appendCommunicationGroups(data)
								: setCommunicationGroups(data),
						);
					},
				],
			}),
		);
	};

export const { loadSorted, loadMore, loadPage, reload } = generateCommonFetchActions(fetchData, url);

export const sortRecords = (sort) => (dispatch) => {
	dispatch(setSort(sort));
	dispatch(loadSorted(sort));
};

export const setInitialFormValues =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		sendControlNext(
			CONTACT_HISTORY_FORM_NAME,
			setValue({ fieldName: FIELDS.dateFrom, value: getDate3MonthsAgoAsDateObj() }),
		);
		sendControlNext(CONTACT_HISTORY_FORM_NAME, setValue({ fieldName: FIELDS.dateTo, value: new Date() }));
	};

export const submitForm =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const data = await getFormData(CONTACT_HISTORY_FORM_NAME);
		const formData = trimFormData(data);
		const queryParams = queryParamsFactory(formData, getSort(getState()));

		dispatch(fetchData({ queryParams, callGtmFilter: true, formData }, appendMode.SET));
	};

export const resetToInitialState = () => (dispatch) => {
	dispatch(reset());
	dispatch(fetch.actions.clearFetchStore(url));
};

export const resetCriteria =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(reset());
		sendControlNext(CONTACT_HISTORY_FORM_NAME, resetForm());
		dispatch(setInitialFormValues());
		dispatch(submitForm());
	};

export const loadWithQuickFilter =
	(type) =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(changeQuickFilter(type));

		let service;
		switch (type) {
			case quickFilter.CANCEL_REQUEST:
				service = SERVICES.cancelingRequest;
				break;
			case quickFilter.SERVICES_SERVICES:
				service = SERVICES.serviceServices;
				break;
			case quickFilter.BUSINESS_SERVICES:
				service = SERVICES.businessServices;
				break;
		}

		sendControlNext(CONTACT_HISTORY_FORM_NAME, resetForm());
		dispatch(setInitialFormValues());
		sendControlNext(CONTACT_HISTORY_FORM_NAME, setValue({ fieldName: FIELDS.service, value: service }));

		dispatch(submitForm());
	};

export const exportToCsv = () => async (dispatch, getState) => {
	const state = getState();
	const contextUser = userContext.selectors.getContextUser(state);
	const { queryParams } = fetch.selectors.getLastRequest(url)(getState());
	// eslint-disable-next-line no-unused-vars
	const { page, size, sort, ...rest } = queryParams;
	const response = await dispatch(
		fetch.actions.postBinary(fetch.constants.urls.communicationHistoryExport, {
			queryParams: { ...rest },
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
		}),
	);

	saveFile({ response });
};
